<template>
  <div :class="$style.task">
    <div :class="{[$style.taskIcon]: task.hasManualCheck}">
      <Icon
        v-if="task.complete"
        :class="$style.complete"
        icon="checkmarkCircle"
        inline
      />
      <div
        v-else-if="!task.complete"
        :class="$style.checklistTaskNumber"
      >
        {{ taskIndex + 1 }}
      </div>
    </div>
    <span :class="$style.taskText">
      <Button
        v-if="task.defaultUrl"
        :class="{
          [$style.button]: true,
          [$style.taskComplete]: task.complete
        }"
        hasNoBackground
        :text="task.headerText"
        textCase="inherit"
        type="informational"
        @click="handleClick(task.taskType, task.headerText)"
      />
      <ParagraphBody
        v-else
        :class="{
          [$style.taskLinkInactive]: true,
          [$style.taskComplete]: task.complete
        }"
      >
        {{ task.headerText }}
      </ParagraphBody>
      <Checkbox
        v-if="task.hasManualCheck"
        :class="$style.manualCheckbox"
        label="Check off when completed"
        :value="task.complete"
        @input="handleCheckboxChange(task.taskType, taskIndex, !task.complete)"
      />
    </span>
    <TooltipIcon
      v-if="task.tooltip && task.tooltip.video"
      :class="$style.tooltip"
      :linkHref="task.tooltip.video"
      :linkText="task.tooltip.title"
      orientation="left"
      text="Watch Video:"
    />
    <TooltipIcon
      v-else-if="task.tooltip && task.tooltip.href"
      :class="$style.tooltip"
      :linkHref="task.tooltip.href"
      :linkText="task.tooltip.title"
      orientation="left"
      text="View the detailed guide for"
    />
    <TooltipIcon
      v-else-if="task.tooltip"
      :class="$style.tooltip"
      orientation="left"
      :text="task.tooltip.text"
    />
  </div>
</template>

<script>
import {
  Button,
  Checkbox,
  Icon,
  ParagraphBody,
  TooltipIcon,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { implementationChecklistRegion } from 'client/components/ImplementationChecklist/ImplementationChecklistConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  onboardingFeatures,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { tasks } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import Util from 'client/util/Util';

const onboardingModule = 'OnboardingModule';

export default {
  name: 'ImplementationChecklistTask',

  components: {
    Button,
    Checkbox,
    Icon,
    ParagraphBody,
    TooltipIcon,
  },

  onboardingFeatures,
  tasks,

  props: {
    checklist: {
      required: true,
      type: Object,
      validator: function validator(value) {
        if (!value) {
          return false;
        }

        if (!value.tasks || !value.feature) {
          return false;
        }

        return true;
      },
    },
    task: {
      required: true,
      type: Object,
    },
    taskIndex: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    ...mapGetters(onboardingModule, {
      implementationChecklistDismissed: 'getImplementationChecklistDismissed',
    }),

    ...mapState(onboardingModule, [
      'tasksUrls',
    ]),
  },

  methods: {
    ...mapActions(onboardingModule, [
      'updateImplementationChecklistDismissed',
      'updateImplementationTaskStatus',
      'updateTaskUrl',
    ]),

    handleCheckboxChange(taskType, taskIndex, complete) {
      this.updateImplementationTaskStatus({
        checklist: this.checklist,
        taskIndex,
        taskType,
        complete,
      });

      const segmentEvent = {
        value: complete,
        description: 'Toggles the status of an individual task on the implementation checklist.',
        page: this.$page,
        region: implementationChecklistRegion,
        section: `${this.checklist.headerText} Card`,
        text: this.task.headerText,
      };
      this.$emit('event', segmentEvent);
    },

    async handleClick(taskType, linkText) {
      await this.updateTaskUrl({ taskType });
      const url = this.tasksUrls.get(taskType);

      if (url) {
        Util.redirect(url);
      }

      if (this.implementationChecklistDismissed) {
        this.updateImplementationChecklistDismissed(false);
      }

      UiEvents.triggerLinkClicked({
        description: 'Navigates user to a specific page',
        page: this.$page,
        region: implementationChecklistRegion,
        section: `${this.checklist.headerText} Card`,
        text: linkText,
        toUrl: url,
      });
    },
  },
};
</script>

<style module scoped>
.button.button {
  text-align: left;
}

.taskComplete.taskComplete /deep/ span {
  text-decoration: line-through;
}

.button.button:hover /deep/ span {
  border-bottom: none !important;
  text-decoration: underline !important;
}

.checklistTaskNumber {
  align-items: center;
  border: var(--jc-border-width-double) solid var(--jc-color-disabled-text);
  border-radius: 50%;
  color: var(--jc-color-disabled-text);
  display: flex;
  font-size: var(--jcBodySmall);
  font-weight: bold;
  height: 20px;
  justify-content: center;
  margin-bottom: 0;
  margin-right: var(--jc-spacer-small);
  margin-top: var(--jc-spacer-x-small);
  text-align: center;
  width: 20px;
}

.complete.complete {
  color: var(--jc-color-brand-500);
  font-size: 20px; /* workaround for legacy styles */
  margin-right: 10px !important;
}

.manualCheckbox {
  margin-top: 12px;
}

.manualCheckbox:last-child {
  color: var(--jcGrey);
}

.task {
  align-items: center;
  display: flex;
}

.taskComplete {
  text-decoration: line-through;
}

.taskIcon {
  align-self: flex-start;
}

.taskLinkInactive {
  font-weight: 600;
}

.taskText {
  padding-top: 0.25em;
}

.task:not(:last-child) {
  margin-bottom: var(--jc-spacer-medium);
}

.tooltip {
  --tooltip-max-width: 30ch;
}
</style>
